/**
 * Bills in the Needs You queue (v8.0.0, Blueprint §9, §13).
 *
 * Two cards, in the same ONE queue as the suggestions and the deposits (§13: one
 * queue, never two):
 *
 *   · "Looks like a bill" — a stream the bank sees repeating. Track it (one tap
 *     when the bank gave an amount) or say it is not a bill. Nothing is tracked
 *     until the operator answers, and nothing is pre-selected.
 *   · "Paid" — a tracked bill the bank has seen paid. One tap clears that cycle's
 *     reserve back to free cash and moves the due date on.
 *
 * Not the `proposal` class, for the same reason as the deposit card: that class
 * means "a staged suggestion that moves money", and the browser suite counts on
 * it. These share the look through components.css instead.
 */

import type { ApiBill, ApiPaidBill } from '../lib/api';
import { billCardCopy, forwardCopy, paidCardCopy } from '../lib/bill-copy';
import { formatMoney } from '../lib/envelope-math';
import type { ForwardView } from '../lib/forward';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

export interface BillHandlers {
  onTrack: (bill: ApiBill) => void;
  onDismiss: (bill: ApiBill) => void;
}

export function createBillCard(bill: ApiBill, handlers: BillHandlers): HTMLElement {
  const copy = billCardCopy(bill);
  const card = el('section', 'card bill-card');
  card.dataset.billId = bill.id;
  if (bill.provisional) card.dataset.provisional = 'true';

  card.append(el('div', 'label', copy.label));
  const amount = el('div', 'proposal-amount', copy.amountText);
  amount.setAttribute('aria-label', copy.amountLabel);
  card.append(amount);
  card.append(el('div', 'proposal-route', copy.route));
  if (copy.bankText) card.append(el('p', 'soft proposal-memo', copy.bankText));
  if (copy.provisionalNote) card.append(el('p', 'soft proposal-memo', copy.provisionalNote));
  card.append(el('p', 'deposit-question', copy.question));

  const actions = el('div', 'proposal-actions');
  const track = el('button', 'btn-primary', copy.trackLabel);
  track.type = 'button';
  track.setAttribute('aria-label', copy.trackAria);
  track.addEventListener('click', () => handlers.onTrack(bill));
  const dismiss = el('button', 'btn-quiet', copy.dismissLabel);
  dismiss.type = 'button';
  dismiss.setAttribute('aria-label', copy.dismissAria);
  dismiss.addEventListener('click', () => handlers.onDismiss(bill));
  actions.append(track, dismiss);
  card.append(actions);
  return card;
}

export function createPaidBillCard(
  paid: ApiPaidBill,
  onSettle: (paid: ApiPaidBill) => void,
): HTMLElement {
  const copy = paidCardCopy(paid);
  const card = el('section', 'card bill-card bill-paid');
  card.dataset.billId = paid.id;
  card.append(el('div', 'label', copy.label));
  card.append(el('div', 'bill-paid-title', copy.title));
  card.append(el('p', 'soft proposal-memo', copy.detail));
  const actions = el('div', 'proposal-actions');
  const settle = el('button', 'btn-primary', copy.action);
  settle.type = 'button';
  settle.setAttribute('aria-label', copy.actionAria);
  settle.addEventListener('click', () => onSettle(paid));
  actions.append(settle);
  card.append(actions);
  return card;
}

/**
 * The forward view: what is due in the next 30 days, as one quiet card (§9).
 * A list, not a set of tiles — it is a reading flow (A.6), and every line says
 * the same three things: what, when, how much of it is set aside.
 */
export function createForwardCard(view: ForwardView): HTMLElement {
  const copy = forwardCopy(view);
  const card = el('section', 'card vitals forward-card');
  card.setAttribute('aria-labelledby', 'forward-heading');
  const heading = el('h2', 'label', copy.heading);
  heading.id = 'forward-heading';
  card.append(heading);
  card.append(el('p', 'forward-summary', copy.summary));

  const list = el('ul', 'forward-list');
  for (const item of view.items) {
    const li = el('li', 'forward-item');
    li.dataset.tone = item.daysUntilDue < 0 ? 'past' : item.daysUntilDue <= 7 ? 'soon' : 'later';
    li.append(el('span', 'forward-name', item.name));
    const share =
      item.percentSetAside == null ? 'waiting on your bank' : `${item.percentSetAside}% set aside`;
    li.append(
      el('span', 'forward-meta', `${formatMoney(item.targetMinor)} · ${item.dueText} · ${share}`),
    );
    list.append(li);
  }
  card.append(list);
  return card;
}
